/**
 * Whole-tyre layout: where the tyres of a pile sit, and how one of them is drawn.
 *
 * Used by the painter (./trackPainter.js) and by the editor's own canvas, so the editor's preview
 * piles its tyres exactly where the exported picture will: both hand over ONE strip at a time (the
 * painter chains a material's strips together only for the shape they make, see paintBarriers), so
 * the same wall gives the same tyres, colours and aligned grid in both.
 *
 * Each whole upright sprite fits inside a `unit`-diameter circle. Grid spacing is exactly `unit`;
 * only complete cells that fit the obstacle are used. Leftover space stays empty, with no clipping,
 * squeezing or stagger. The circle bound also keeps upright artwork inside diagonal obstacles.
 */
import { widthOf } from './trackGeometry.js';

/** Total length of a chain of `{x, y}` points. */
export function chainLength(chain) {
  let total = 0;
  for (let i = 0; i < chain.length - 1; i++) {
    total += Math.hypot(chain[i + 1].x - chain[i].x, chain[i + 1].y - chain[i].y);
  }
  return total;
}

/** Where a chain is at arc-length `s` from its start: position, direction and width there. */
export function alongChain(chain, s) {
  let travelled = 0;
  for (let i = 0; i < chain.length - 1; i++) {
    const a = chain[i], b = chain[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const length = Math.hypot(dx, dy);
    if (!(length > 0.01)) continue;
    if (s <= travelled + length) {
      const t = Math.max(0, Math.min(1, (s - travelled) / length));
      return {
        x: a.x + dx * t,
        y: a.y + dy * t,
        angle: Math.atan2(dy, dx),
        width: widthOf(a, 20) + (widthOf(b, 20) - widthOf(a, 20)) * t,
      };
    }
    travelled += length;
  }
  return null;
}

/** Centres of complete, equally spaced cells. Centre the grid in the available length. */
export function tyreSpread(length, unit) {
  if (!(unit > 0) || !Number.isFinite(length)) return [];
  const count = Math.max(0, Math.floor((length + 1e-9) / unit));
  const padding = Math.max(0, (length - count * unit) / 2);
  return Array.from({ length: count }, (_, i) => padding + unit * (i + 0.5));
}

/** Shared global depth order, including stacks belonging to different obstacles/materials. */
export const compareTyreDepth = (a, b) => a.y - b.y || a.x - b.x;

/**
 * A material's tyres, ready to stamp: the sprite sheet the look points at, plus its cell grid. Nothing
 * is masked here — each sprite is already one whole tyre with transparency around it.
 *
 * `ink` is the diameter of the circle enclosing every non-transparent pixel of every sheet cell.
 * All colours share that scale, so their complete silhouettes fit even when a barrier is diagonal
 * while the sprites stay upright. Transparent padding is retained rather than clipping the image.
 */
export function tyreSet(look, image, createCanvas) {
  const { cellWidth, cellHeight, columns = 1, rows = 1 } = look.image ?? {};
  if (!image || !cellWidth || !cellHeight) return null;
  return {
    tile: image, cellWidth, cellHeight, columns, rows,
    unit: look.unit ?? 10,
    colours: look.tyreColours ?? 'black',
    ink: measureInk(createCanvas, image, cellWidth, cellHeight, columns, rows),
  };
}

/** Enclosing diameter, including antialiased edges. An unreadable sheet uses its cell diagonal. */
function measureInk(createCanvas, image, cellWidth, cellHeight, columns, rows) {
  try {
    const w = cellWidth * columns, h = cellHeight * rows;
    const canvas = createCanvas(w, h);
    const ctx = canvas.getContext('2d');
    // This measures source pixels, not world units: a high-resolution tile factory may pre-scale ctx.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(image, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h).data;
    let radius = 0;
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        if (!data[(y * w + x) * 4 + 3]) continue;
        const dx = Math.abs((x % cellWidth) + 0.5 - cellWidth / 2) + 0.5;
        const dy = Math.abs((y % cellHeight) + 0.5 - cellHeight / 2) + 0.5;
        radius = Math.max(radius, Math.hypot(dx, dy));
      }
    }
    return radius > 0 ? radius * 2 : Math.hypot(cellWidth, cellHeight);
  } catch {
    return Math.hypot(cellWidth, cellHeight);
  }
}

/**
 * One tyre of a pile, out of the material's sprite sheet, centred on the stamp's position. The sprite's
 * own silhouette IS the tyre, so nothing is clipped or masked here.
 *
 * `column` and `row` are the cell of the sheet (see tyreCell): the column is the variant, the row the
 * colour.
 */
export function drawTyreCell(ctx, set, stamp) {
  const { tile, cellWidth, cellHeight, columns, rows, unit, ink } = set;
  const side = (unit * cellWidth) / (ink || cellWidth);
  const positive = (n, m) => ((n % m) + m) % m;
  ctx.drawImage(tile, positive(stamp.column, columns) * cellWidth, positive(stamp.row, rows) * cellHeight,
    cellWidth, cellHeight, -side / 2, -side / 2, side, side);
}

/** Two complete red stacks, then two white stacks, in every row. */
const COLOUR_RUN = 2;

/**
 * Which cell of the sheet a tyre takes, from its ordinal in the pile: `alongIndex` tyres along the wall,
 * `acrossIndex` across it and `set` the selected colour mode.
 *
 * The choice is made from those numbers and never from where the pile sits in the world: dragging a
 * rectangle has to move the drawing with it, not repaint it (a world-anchored choice gave the colour at
 * a given height of the map, so two walls set side by side could not be made to alternate at all).
 *
 * Alternate always runs along the barrier, including barriers several rows deep. Random uses stable
 * obstacle identity and grid indices, so moving an obstacle or opening Preview does not recolour it.
 * Sheet rows are black=0, red=1, white=2; random favours black with a 75 percent probability.
 */
function tyreCell(alongIndex, acrossIndex, set) {
  if (set.colours === 'alternate') {
    return { column: 0, row: Math.floor(alongIndex / COLOUR_RUN) % 2 === 0 ? 1 : 2 };
  }
  if (set.colours === 'random') {
    let hash = Math.imul(alongIndex + 1, 0x9e3779b1)
      ^ Math.imul(acrossIndex + 1, 0x85ebca6b) ^ (set.seed ?? 0);
    hash = Math.imul(hash ^ (hash >>> 16), 0x7feb352d);
    hash = Math.imul(hash ^ (hash >>> 15), 0x846ca68b);
    const value = ((hash ^ (hash >>> 16)) >>> 0) / 4294967296;
    return { column: 0, row: value < 0.75 ? 0 : value < 0.875 ? 1 : 2 };
  }
  return { column: 0, row: 0 };
}

function tyreSeed(id) {
  let seed = 2166136261;
  for (const ch of String(id ?? 0)) seed = Math.imul(seed ^ ch.charCodeAt(0), 16777619);
  return seed >>> 0;
}

/** Kept for callers of the old helper: grid rows are never staggered. */
export function tyreLineShift() { return 0; }

/** A whole radius-sized circle must fit one segment's strip/capsule, with square outer end caps. */
function circleFitsChain(x, y, radius, chain) {
  for (let j = 0; j < chain.length - 1; j++) {
    const a = chain[j], b = chain[j + 1];
    const dx = b.x - a.x, dy = b.y - a.y, length = Math.hypot(dx, dy);
    if (!(length > 0.01)) continue;
    const along = ((x - a.x) * dx + (y - a.y) * dy) / length;
    if (j === 0 && along < radius - 1e-8) continue;
    if (j === chain.length - 2 && along > length - radius + 1e-8) continue;
    const t = Math.max(0, Math.min(1, along / length));
    const room = Math.min(widthOf(a, 20), widthOf(b, 20)) / 2 - radius;
    if (room < -1e-8) continue;
    if (Math.hypot(x - a.x - dx * t, y - a.y - dy * t) <= room + 1e-8) return true;
  }
  return false;
}

/**
 * The tyres of a chain, as stamps to draw: position and the cell of the sheet each one takes.
 * The stack artwork stays upright in world space, regardless of the barrier's direction.
 * No drawing here — the painter draws the shadows and the bodies in two separate passes (see
 * paintBarriers), and the editor draws them themselves.
 *
 * Empty margins and bend cells that cannot hold a whole stack stay empty. A spatial grid rejects
 * overlapping candidates at bends without changing the spacing or staggering the other rows.
 */
export function tyreChainStamps(chain, set) {
  const unit = set.unit;
  const total = chainLength(chain);
  const stamps = [];
  const occupied = new Map();
  if (!(total > 0)) return stamps;

  const along = tyreSpread(total, unit);
  for (let i = 0; i < along.length; i++) {
    const base = alongChain(chain, along[i]);
    if (!base) continue;
    const across = tyreSpread(base.width, unit);
    const half = base.width / 2;
    const nx = -Math.sin(base.angle), ny = Math.cos(base.angle);
    for (let k = 0; k < across.length; k++) {
      const offset = across[k] - half;
      const x = base.x + nx * offset;
      const y = base.y + ny * offset;
      if (!circleFitsChain(x, y, unit / 2, chain)) continue;
      const gx = Math.floor(x / unit), gy = Math.floor(y / unit);
      let overlaps = false;
      for (let cx = gx - 1; cx <= gx + 1 && !overlaps; cx++) {
        for (let cy = gy - 1; cy <= gy + 1 && !overlaps; cy++) {
          overlaps = (occupied.get(`${cx},${cy}`) ?? []).some(p => (p.x - x) ** 2 + (p.y - y) ** 2 < unit ** 2 - 1e-8);
        }
      }
      if (overlaps) continue;
      const stamp = { x, y, angle: 0, alongIndex: i, acrossIndex: k, ...tyreCell(i, k, set) };
      stamps.push(stamp);
      const key = `${gx},${gy}`, cell = occupied.get(key) ?? [];
      cell.push(stamp); occupied.set(key, cell);
    }
  }
  return stamps.sort(compareTyreDepth);
}

/** A centred square grid, keeping only whole stacks whose enclosing circles fit the obstacle. */
export function tyreCircleStamps(circle, set) {
  set = { ...set, seed: tyreSeed(circle.id) };
  const unit = set.unit;
  const { x, y, radius } = circle;
  const positions = tyreSpread(radius * 2, unit);
  const count = positions.length;
  const stamps = [];
  for (let k = 0; k < count; k++) {
    for (let i = 0; i < count; i++) {
      const cx = x + positions[i] - radius;
      const cy = y + positions[k] - radius;
      if (Math.hypot(cx - x, cy - y) + unit / 2 > radius + 1e-8) continue;
      // Same cells as a chain, off the tyre's place in the stack (see tyreCell), so a stack and a wall
      // are made of the same drawings; a stack's grid is as many lines deep as it is wide.
      stamps.push({ x: cx, y: cy, angle: 0, alongIndex: i, acrossIndex: k, ...tyreCell(i, k, set) });
    }
  }
  return stamps;
}

/**
 * The line a rectangle obstacle runs along, in world coordinates, with the thickness across it.
 *
 * In a rect's own frame the WIDTH runs along its angle and the height across it, so the spine follows the
 * long side — whichever of the two that is — along ITS OWN axis: picking the longer number and rotating
 * that by the angle instead lays every tall block on its side (hot_road's 40x1487 pit wall came out as a
 * 1487-long wall lying across the track).
 *
 * A SPRITE material (`alongWidth`) is the exception, and the rule has to name it: a grandstand, a block
 * of houses, is often deeper than it is long, and "the long side is the road" would lay the seating
 * across it. For those the width IS the direction it runs and the height is the depth, which is also the
 * ratio the editor keeps the rectangle to (see materialsForType).
 */
export function rectSpine(obstacle, alongWidth = false) {
  const angle = obstacle.angle ?? 0;
  const horizontal = alongWidth || obstacle.width >= obstacle.height;
  const along = (horizontal ? obstacle.width : obstacle.height) / 2;
  const ux = horizontal ? Math.cos(angle) : -Math.sin(angle);
  const uy = horizontal ? Math.sin(angle) : Math.cos(angle);
  return {
    points: [
      { x: obstacle.x - ux * along, y: obstacle.y - uy * along },
      { x: obstacle.x + ux * along, y: obstacle.y + uy * along },
    ],
    thickness: horizontal ? obstacle.height : obstacle.width,
  };
}

/**
 * One obstacle's tyres, as stamps: the whole of what a tyre wall, block, polygon or stack is. The editor
 * and painter both use this, feeding one obstacle at a time, so grid indices and random colour seeds
 * agree in the editor and in Preview.
 *
 * A polygon's `points` is what the game reads (curves already flattened); `nodes` is the same thing with
 * the Bezier handles kept for the editor.
 */
export function tyreStampsForObstacle(obstacle, set) {
  if (!set || !obstacle) return [];
  set = { ...set, seed: tyreSeed(obstacle.id) };
  if (obstacle.type === 'circle') return tyreCircleStamps(obstacle, set);
  if (obstacle.type === 'rect') {
    // The tyres always run along the rectangle's WIDTH (rectSpine's `alongWidth`), never along its long
    // side: a block of tyres is not a wall, and the wall rule would lay the lines the other way round as
    // soon as the block is taller than it is wide. Each row follows the same width axis and colour run.
    const { points, thickness } = rectSpine(obstacle, true);
    return tyreChainStamps(points.map((point) => ({ ...point, width: thickness })), set);
  }
  if (obstacle.type === 'poly') {
    const source = obstacle.points?.length ? obstacle.points : obstacle.nodes ?? [];
    if (source.length < 2) return [];
    const thickness = obstacle.thickness ?? 8;
    return tyreChainStamps(source.map((point) => ({ x: point.x, y: point.y, width: thickness })), set);
  }
  if (obstacle.type === 'wall') {
    const thickness = obstacle.thickness ?? 20;
    return tyreChainStamps([
      { x: obstacle.x1, y: obstacle.y1, width: thickness },
      { x: obstacle.x2, y: obstacle.y2, width: thickness },
    ], set);
  }
  return [];
}
